import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconBellRinging,
  IconGripVertical,
  IconHeart,
  IconX,
} from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { RoomKnock } from "../lib/api";
import type { FriendLink } from "../../shared/types";

const PANEL_WIDTH = 320;
const PANEL_HEIGHT = 360;

function clampPosition(x: number, y: number): { x: number; y: number } {
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
  const maxY = Math.max(margin, window.innerHeight - PANEL_HEIGHT - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function initialPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 16, y: 200 };
  return clampPosition(16, window.innerHeight - PANEL_HEIGHT - 80);
}

interface Props {
  knocks: RoomKnock[];
  actionId: string | null;
  onAdmit: (knockId: string) => void;
  onReject: (knockId: string) => void;
  pendingFriends: FriendLink[];
  selfAddress: string;
  onAcceptFriend: (friend: FriendLink) => void;
  onClose: () => void;
}

export function KnocksPanel({
  knocks,
  actionId,
  onAdmit,
  onReject,
  pendingFriends,
  selfAddress,
  onAcceptFriend,
  onClose,
}: Props) {
  const [position, setPosition] = useState(initialPosition);
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setPosition(clampPosition(state.originX + dx, state.originY + dy));
  }, []);

  const onDragEnd = useCallback(
    function handleDragEnd() {
      stopDragging();
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    },
    [onDragMove, stopDragging]
  );

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
      stopDragging();
    };
  }, [onDragEnd, onDragMove, stopDragging]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  };

  const totalCount = knocks.length + pendingFriends.length;

  return (
    <Paper
      p={10}
      radius="md"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        maxHeight: PANEL_HEIGHT,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        style={{
          touchAction: "none",
          cursor: "grab",
          marginBottom: 6,
          padding: "6px 10px",
          userSelect: "none",
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={14} color="rgba(0,0,0,0.3)" />
            <IconBellRinging size={15} color="#ff922b" />
            <Title order={5} fz={13} c="gray.8">
              Notifications ({totalCount})
            </Title>
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray.4"
            onClick={onClose}
          >
            <IconX size={15} />
          </ActionIcon>
        </Group>
      </Box>

      <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars>
        <Stack gap={8} px={10}>
          {/* Friend requests */}
          {pendingFriends.length > 0 && (
            <>
              <Divider
                label="Friend Requests"
                labelPosition="left"
                color="gray.3"
                styles={{
                  label: { fontSize: 11, color: "var(--mantine-color-gray-5)" },
                }}
              />
              {pendingFriends.map((f) => {
                const other =
                  f.userAddress === selfAddress
                    ? f.friendAddress
                    : f.userAddress;
                return (
                  <Box
                    key={`${f.userAddress}:${f.friendAddress}`}
                    p="xs"
                    style={{
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.06)",
                      background: "rgba(253,126,20,0.03)",
                    }}
                  >
                    <Group gap={6} mb={4}>
                      <IconHeart size={13} color="#ff922b" />
                      <Text size="xs" fw={600} c="dark" truncate>
                        {f.displayName ||
                          `${other.slice(0, 10)}...${other.slice(-6)}`}
                      </Text>
                    </Group>
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="orange"
                      onClick={() => onAcceptFriend(f)}
                    >
                      Accept
                    </Button>
                  </Box>
                );
              })}
            </>
          )}

          {/* Knocks */}
          {knocks.length > 0 && (
            <>
              <Divider
                label="Knocks"
                labelPosition="left"
                color="gray.3"
                styles={{
                  label: { fontSize: 11, color: "var(--mantine-color-gray-5)" },
                }}
              />
              {knocks.map((knock) => (
                <Box
                  key={knock.id}
                  p="xs"
                  style={{
                    borderRadius: 8,
                    border: "1px solid rgba(0,0,0,0.06)",
                    background: "rgba(0,0,0,0.01)",
                  }}
                >
                  <Text size="xs" fw={600} c="dark" truncate mb={2}>
                    {knock.requesterAddress.slice(0, 10)}...
                    {knock.requesterAddress.slice(-6)}
                  </Text>
                  {knock.message && (
                    <Text size="xs" c="gray.6" lineClamp={2} mb={4}>
                      {knock.message}
                    </Text>
                  )}
                  <Group gap={6}>
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="teal"
                      loading={actionId === knock.id}
                      onClick={() => onAdmit(knock.id)}
                    >
                      Admit
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      loading={actionId === knock.id}
                      onClick={() => onReject(knock.id)}
                    >
                      Reject
                    </Button>
                  </Group>
                </Box>
              ))}
            </>
          )}

          {totalCount === 0 && (
            <Text size="xs" c="dimmed" ta="center" py="sm">
              No notifications.
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Paper>
  );
}
